<script>
	import { onMount, onDestroy } from "svelte";
	import { navigate } from "svelte-routing";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import csvDownload from "json-to-csv-export";
	import queryString from "query-string";
	import PageHeader from "../../../components/PageHeader/PageHeader.svelte";
	import CustomTable from "../../../components/CustomTable/CustomTable.svelte";
	import CustomSearch from "../../../components/CustomSearch/CustomSearch.svelte";
	import StatusBadgeItem from "../../../components/CustomSearch/StatusBadgeItem.svelte";
	import StatusBadgeSelection from "../../../components/CustomSearch/StatusBadgeSelection.svelte";
	import CustomDatePicker from "../../../components/CustomDatePicker/CustomDatePicker.svelte";
	import CustomButton from "../../../components/CustomButton/CustomButton.svelte";
	import SearchIcon from "../../../components/SearchIcon/SearchIcon.svelte";
	import CreateIssueModal from "./CreateIssueModal.svelte";
	import { brand } from "src/brand";
	import { isMnrPhase2Enabled } from "src/config/flag-config";
	import { getMaintenanceRepairVehicles } from "src/services/maintenanceRepair";
	import { fetchTaskLookups } from "src/services/lookupCache";
	import {
		daysElapsed,
		formatDateTime,
		normalizeVehicleStatusLabel,
		omitEmpty,
		vehicleStatusLabel,
		yesNoToBoolean,
	} from "src/utils/misc";
	import {
		vehicleStatusOptions,
		vehicleStatusVariantByLabel,
		readyForCollectionOptions,
		flaggedOptions,
		pageSizeOptions,
	} from "src/utils/options";
	import {
		cachedTaskLookups,
		initPage,
		loading,
		sizeSelect,
		totalPages,
		totalRecords,
	} from "../../../store/store";

	let vehicles = [];
	let error = null;
	let mounted = false;
	let unsubPage;

	const platformSelectOptions = brand.platformOptions.map((option) => ({
		value: option.label,
		label: option.label,
	}));
	const vehicleStatusOrder = vehicleStatusOptions.map((option) =>
		option.label.toLowerCase()
	);
	const vehicleStatusRank = (label) => {
		const rank = vehicleStatusOrder.indexOf(label.trim().toLowerCase());
		return rank === -1 ? vehicleStatusOrder.length : rank;
	};
	$: vehicleStatusSelectOptions = ($cachedTaskLookups?.vehicleStatuses || [])
		.map((item) => ({ value: item.id, label: vehicleStatusLabel(item) }))
		.sort(
			(a, b) => vehicleStatusRank(a.label) - vehicleStatusRank(b.label)
		);
	let isCreateIssueModalOpen = false;

	const DEFAULT_FILTERS = {
		plateNumber: "",
		lastUpdatedFrom: "",
		lastUpdatedTo: "",
		platform: "",
		flagged: "",
		readyForCollection: "",
		vehicleStatus: "",
	};
	const DEFAULT_PAGE_SIZE = "10";
	const pageSizeValues = pageSizeOptions.map((option) => option.value);
	const SORT_COLUMNS = ["lastUpdatedAt"];
	const SORT_DIRECTIONS = {
		asc: "ascending",
		ascending: "ascending",
		desc: "descending",
		descending: "descending",
	};

	let draft = { ...DEFAULT_FILTERS };
	let applied = { ...DEFAULT_FILTERS };
	let sortColumn = "";
	let sortDirection = "ascending";
	let requestId = 0;
	let datePickerKey = 0;

	function readUrl() {
		const q = queryString.parse(window.location.search);
		const page = Number(q.page);
		initPage.set(Number.isInteger(page) && page > 0 ? page : 1);
		sizeSelect.set(
			pageSizeValues.includes(String(q.size))
				? String(q.size)
				: DEFAULT_PAGE_SIZE
		);
		sortColumn = SORT_COLUMNS.includes(String(q.sortCol))
			? String(q.sortCol)
			: "";
		sortDirection =
			(sortColumn && SORT_DIRECTIONS[q.sortDir]) || "ascending";
		for (const key in DEFAULT_FILTERS) {
			if (q[key]) draft[key] = String(q[key]);
		}
		applied = { ...draft };
	}

	function syncUrl() {
		const qs = queryString.stringify(
			omitEmpty({
				...applied,
				page: $initPage > 1 ? $initPage : "",
				size: $sizeSelect === DEFAULT_PAGE_SIZE ? "" : $sizeSelect,
				sortCol: sortColumn,
				sortDir: sortColumn ? sortDirection : "",
			})
		);
		window.history.replaceState(
			null,
			"",
			qs ? `?${qs}` : window.location.pathname
		);
	}

	async function fetchVehicles() {
		const id = ++requestId;
		error = null;
		loading.set(true);
		try {
			const { flagged, readyForCollection, ...filters } = applied;
			const params = omitEmpty({
				...filters,
				page: $initPage,
				size: Number($sizeSelect),
				handoverFlagged: yesNoToBoolean(flagged),
				readyForCollection: yesNoToBoolean(readyForCollection),
				sortCol: sortColumn,
				sortDir: sortColumn
					? sortDirection === "descending"
						? "desc"
						: "asc"
					: "",
			});
			const res = await getMaintenanceRepairVehicles(params);
			if (id !== requestId) return;
			vehicles = res.data.data;
			totalRecords.set(res.data.totalCount);
			const pages =
				Math.ceil(res.data.totalCount / Number($sizeSelect)) || 1;
			totalPages.set(pages);
			if ($initPage > pages) {
				initPage.set(pages);
				if (!mounted) return fetchVehicles();
			}
		} catch (err) {
			if (id !== requestId) return;
			error = err;
			vehicles = [];
			totalRecords.set(0);
			totalPages.set(1);
		} finally {
			if (id === requestId) {
				loading.set(false);
				syncUrl();
			}
		}
	}

	readUrl();

	onMount(async () => {
		if (!$cachedTaskLookups) fetchTaskLookups().catch(() => {});
		unsubPage = initPage.subscribe(() => {
			if (mounted) fetchVehicles();
		});
		await fetchVehicles();
		mounted = true;
	});

	onDestroy(() => {
		requestId++;
		loading.set(false);
		unsubPage?.();
		initPage.set(1);
		sizeSelect.set("20");
		totalPages.set(1);
		totalRecords.set(0);
	});

	function handleSearch() {
		applied = {
			...draft,
			plateNumber: draft.plateNumber.trim().toUpperCase(),
		};
		if ($initPage !== 1) {
			initPage.set(1);
		} else {
			fetchVehicles();
		}
	}

	$: hasFilters = [...Object.values(draft), ...Object.values(applied)].some(
		Boolean
	);

	function handleClear() {
		draft = { ...DEFAULT_FILTERS };
		applied = { ...DEFAULT_FILTERS };
		datePickerKey++;
		if ($initPage !== 1) {
			initPage.set(1);
		} else {
			fetchVehicles();
		}
	}

	const toOption = (options, value) =>
		options.find((option) => option.value === value) ?? null;

	function handlePageSizeChange(option) {
		if (option.value === $sizeSelect) return;
		sizeSelect.set(option.value);
		if ($initPage !== 1) {
			initPage.set(1);
		} else {
			fetchVehicles();
		}
	}

	function handleSort(event) {
		sortColumn = event.detail.column;
		sortDirection = event.detail.direction;
		if ($initPage !== 1) {
			initPage.set(1);
		} else {
			fetchVehicles();
		}
	}

	function getDaysInWorkshop(enteredWorkshopAt) {
		if (!enteredWorkshopAt) return "-";
		return String(daysElapsed(enteredWorkshopAt));
	}

	function toDisplayVehicle(vehicle) {
		const display = buildDisplayVehicle(vehicle);
		return isMnrPhase2Enabled
			? display
			: {
					...display,
					readyForCollectionDisplay: " ",
					lastUpdatedDisplay: " ",
					daysInWorkshopDisplay: " ",
				};
	}

	function buildDisplayVehicle(vehicle) {
		return {
			vehicleId: vehicle.vehicleId,
			plateNumber: vehicle.plateNumber,
			makeAndModel: vehicle.makeAndModel,
			platform: vehicle.platform,
			handoverFlagged: isMnrPhase2Enabled
				? vehicle.handoverFlagged
				: undefined,
			vehicleStatus: normalizeVehicleStatusLabel(vehicle.vehicleStatus),
			homeLotAddress: vehicle.homeLotAddress,
			lastUpdatedDisplay: formatDateTime(vehicle.lastUpdatedAt),
			readyForCollectionDisplay:
				vehicle.readyForCollection === true
					? "Yes"
					: vehicle.readyForCollection === false
						? "No"
						: "-",
			daysInWorkshopDisplay: getDaysInWorkshop(vehicle.enteredWorkshopAt),
			noOfIssues: vehicle.issueCounts.total,
			bodywork: vehicle.issueCounts.bodywork,
			maintenance: vehicle.issueCounts.maintenance,
			nonWorkshop: vehicle.issueCounts.nonWorkshop,
		};
	}

	$: tableRows = vehicles.map(toDisplayVehicle);

	const tableColumns = [
		{
			key: "plateNumber",
			label: "Vehicle plate number",
			type: "flagged",
			className: "link link-text",
			flagKey: "handoverFlagged",
			flagValue: true,
			linkBy: "vehicleId",
			linkTo: "/maintenance-repair/",
		},
		{ key: "makeAndModel", label: "Make and model" },
		{ key: "platform", label: "Platform" },
		{
			key: "readyForCollectionDisplay",
			label: "Ready for collection",
		},
		{
			key: "vehicleStatus",
			label: "Vehicle status",
			type: "badge",
			options: vehicleStatusVariantByLabel,
		},
		{ key: "homeLotAddress", label: "Home lot address" },
		{
			key: "lastUpdatedDisplay",
			label: "Last updated",
			sortCol: "lastUpdatedAt",
		},
		{ key: "daysInWorkshopDisplay", label: "Days in workshop" },
		{ key: "noOfIssues", label: "No. of issues" },
		{ key: "bodywork", label: "Bodywork" },
		{ key: "maintenance", label: "Maintenance" },
		{ key: "nonWorkshop", label: "Non-workshop" },
	];

	function handleExportCsv() {
		csvDownload({
			data: vehicles.map(toDisplayVehicle),
			filename: "MaintenanceAndRepairs.csv",
		});
	}

	function handleCreateIssue() {
		isCreateIssueModalOpen = true;
	}

	function handleRowClick(event) {
		navigate(`/maintenance-repair/${event.detail.vehicleId}`);
	}
</script>

<Row>
	<Col xs={12}>
		<Col className="row d-flex align-items-start mx-0">
			<PageHeader title="Maintenance & Repairs" />
		</Col>

		<div class="filters-row fds-dropdown-list mb-2">
			<div class="filters-group">
				<div class="filter-field filter-field-search">
					<label for="mr-plate-number" class="visually-hidden"
						>Search by vehicle plate number</label
					>
					<div class="search-input-wrapper">
						<SearchIcon />
						<input
							id="mr-plate-number"
							type="text"
							class="form-control getgo input mr-search-input"
							placeholder="Search by vehicle plate number"
							bind:value={draft.plateNumber}
							on:keydown={(e) =>
								e.key === "Enter" && handleSearch()}
						/>
					</div>
				</div>
				<div class="filter-field filter-field-date">
					{#key datePickerKey}
						<CustomDatePicker
							name="mr-last-updated"
							placeholder="Last updated"
							isRange={true}
							value={draft.lastUpdatedFrom && draft.lastUpdatedTo
								? [draft.lastUpdatedFrom, draft.lastUpdatedTo]
								: ""}
							onChange={(e) => {
								draft.lastUpdatedFrom = e.detail[0];
								draft.lastUpdatedTo = e.detail[1];
							}}
						/>
					{/key}
				</div>
				<div class="filter-field">
					<CustomSearch
						label="Filter by platform"
						placeholder="All platforms"
						items={platformSelectOptions}
						value={toOption(platformSelectOptions, draft.platform)}
						onSelect={(e) => (draft.platform = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
					/>
				</div>
			</div>
			<div class="actions-group">
				{#if isMnrPhase2Enabled}
					<CustomButton
						buttonText="Export CSV"
						color="secondary"
						on:click={handleExportCsv}
					/>
				{/if}
				<CustomButton
					buttonText="Create Issue"
					color="secondary"
					on:click={handleCreateIssue}
				/>
			</div>
		</div>

		<div class="filters-row fds-dropdown-list mb-3">
			<div class="filters-group">
				<div class="filter-field">
					<CustomSearch
						label="Filter by flagged status"
						placeholder="Flagged"
						items={flaggedOptions}
						value={toOption(flaggedOptions, draft.flagged)}
						onSelect={(e) => (draft.flagged = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
					/>
				</div>
				<div class="filter-field">
					<CustomSearch
						label="Filter by ready for collection"
						placeholder="Ready for collection"
						items={readyForCollectionOptions}
						value={toOption(
							readyForCollectionOptions,
							draft.readyForCollection
						)}
						onSelect={(e) =>
							(draft.readyForCollection = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
					/>
				</div>
				<div class="filter-field filter-field-status">
					<CustomSearch
						label="Filter by vehicle status"
						placeholder="All vehicle statuses"
						items={vehicleStatusSelectOptions}
						value={toOption(
							vehicleStatusSelectOptions,
							draft.vehicleStatus
						)}
						onSelect={(e) => (draft.vehicleStatus = e.detail.value)}
						isSearchable={false}
						isClearable={false}
						chevron
						Item={StatusBadgeItem}
						Selection={StatusBadgeSelection}
					/>
				</div>
				<div class="clear-filters">
					<CustomButton
						buttonText="Clear filters"
						color="link"
						disabled={!hasFilters}
						on:click={handleClear}
					/>
				</div>
				<CustomButton buttonText="Search" on:click={handleSearch} />
			</div>
			<div class="actions-group">
				<div class="page-size-select">
					<CustomSearch
						label="Items per page"
						items={pageSizeOptions}
						value={toOption(pageSizeOptions, $sizeSelect)}
						onSelect={(e) => handlePageSizeChange(e.detail)}
						isSearchable={false}
						isClearable={false}
						chevron
					/>
				</div>
			</div>
		</div>

		{#if error}
			<div class="alert alert-danger" role="alert">
				Failed to load vehicles. Please try again.
			</div>
		{/if}

		<CustomTable
			columns={tableColumns}
			rows={tableRows}
			useNavigation
			on:sort={handleSort}
			on:rowClick={handleRowClick}
		/>
	</Col>
</Row>

<CreateIssueModal
	isOpen={isCreateIssueModalOpen}
	toggle={() => (isCreateIssueModalOpen = !isCreateIssueModalOpen)}
/>

<style>
	.filters-row {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
	}

	.filters-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		gap: 8px;
		flex: 1 1 auto;
	}

	.actions-group {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		justify-content: flex-end;
		gap: 8px;
		margin-left: auto;
	}

	.actions-group :global(.fds-button.btn-secondary),
	.actions-group :global(.fds-button.btn-primary),
	.filters-group :global(.fds-button.btn-primary) {
		border: 1px solid #d3d3d3;
	}

	.actions-group :global(.fds-button.btn-primary:not(:disabled)),
	.filters-group :global(.fds-button.btn-primary:not(:disabled)) {
		background-color: var(--fds-primary, #003bed);
		color: var(--fds-on-primary, #fafbff);
	}

	.filter-field {
		flex: 0 1 200px;
		min-width: 160px;
	}

	.filter-field-search {
		flex-basis: 260px;
	}

	.clear-filters :global(.fds-button.btn-link) {
		background: none;
		border: none;
		color: var(--fds-on-surface, #00071f);
		text-decoration: none;
		min-width: 0;
	}

	.clear-filters :global(.fds-button.btn-link:disabled) {
		background: none;
		color: var(--fds-on-surface-container-variant, #7d8c8c);
	}

	.filter-field-status {
		flex: 0 0 260px;
	}

	.filter-field-status :global(.selectedItem) {
		padding: 0 40px 0 12px;
	}

	.filter-field-status :global(.listContainer) {
		width: max-content !important;
		min-width: 100%;
		max-height: 320px;
		overflow-x: hidden;
	}

	.filters-row .filter-field :global(.selectContainer input),
	.filters-row .page-size-select :global(.selectContainer input) {
		padding-left: 12px !important;
		padding-right: 36px !important;
	}

	.filters-row :global(input::placeholder) {
		font-size: 14px !important;
		font-weight: 400 !important;
	}

	.filters-row :global(input),
	.filters-row :global(.selectedItem) {
		font-size: 14px !important;
		font-weight: 400 !important;
		letter-spacing: normal !important;
	}

	.filter-field-date {
		flex: 0 0 280px;
	}

	.search-input-wrapper {
		position: relative;
	}

	.search-input-wrapper :global(.form-control.getgo.input.mr-search-input) {
		padding-left: 36px;
	}

	.page-size-select {
		width: 140px;
	}
</style>
